import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Loader, Wrapper } from "../../components";
import { useSelector } from "react-redux";
import {
  apiCategortList,
  getAllApisWithSubscription,
  Logs,
} from "../../services";

const hostname = window.location.hostname;
const stageHostnames = ["localhost", "127.0.0.1"];

const baseMap = {
  "customer-test.finhub.habilelabs.io": "https://endpoint-gateway-test.finhub.habilelabs.io/vkyc/dashboard",
  "finhub-stage.habilelabs.io": "https://customer-vkyc-stage.finhub.habilelabs.io/vkyc/dashboard",
  "customer.finhub.habilelabs.io": "https://customer-vkyc.finhub.habilelabs.io/vkyc/dashboard"
};

const baseURL = stageHostnames.includes(hostname)
  ? "https://endpoint-gateway-test.finhub.habilelabs.io/vkyc/dashboard"
  : baseMap[hostname] || "https://customer-vkyc.finhub.habilelabs.io/vkyc/dashboard";

const getStatusLabel = (isSubscribed) =>
  isSubscribed ? "Subscribed" : "Not Activated";

const getStatusBgColor = (isSubscribed) =>
  isSubscribed ? "#16a249" : "#F3F4F6";

const getStatusTextColor = (isSubscribed) =>
    isSubscribed ? "#fff" : "#4B5563";

const StyledCard = styled(Card)(() => ({
  borderRadius: "16px",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  borderLeft: "4px solid #10b981",
  transition: "all 0.2s ease-in-out",
  cursor: "pointer",
  height: "100%",
  "&:hover": {
    boxShadow: "0 8px 25px -8px rgba(0, 0, 0, 0.15)",
    transform: "translateY(-4px)",
  },
}));


const CategoryCard = ({ api, user }) => {
  const role = user?.role?.toLowerCase();

  const isAllowedRole = role === "admin" || role === "vkyc auditor";
  const isSubscribed = api?.subscribe === true;
  const isClickable = isAllowedRole && isSubscribed;

  const handleCardClick = () => {
    if (!isClickable) return;
    const sourceURL = window.location.origin;
    window.open(
      `${baseURL}?token=${user.jwt}&source=${sourceURL}`,
      "_blank"
    );
  };

  return (
    <StyledCard
      onClick={handleCardClick}
      sx={{
        maxWidth: 360,
        cursor: isClickable ? "pointer" : "not-allowed",
        opacity: isClickable ? 1 : 0.7,
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Status */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Chip
            label={getStatusLabel(isSubscribed)}
            size="small"
            sx={{
              backgroundColor: getStatusBgColor(isSubscribed),
              color: getStatusTextColor(isSubscribed),
              fontSize: "11px",
              fontWeight: 600,
              height: 24,
            }}
          />
        </Box>

        {/* Title */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <VerifiedIcon sx={{ color: "#10b981", mr: 1 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1e293b",
              fontSize: "18px",
            }}
          >
            {api?.name}
          </Typography>
        </Box>

        {/* Category */}
        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
          <Chip
            label={api?.category}
            size="small"
            sx={{
              backgroundColor: "#F1F5F9",
              color: "#0F1729",
              fontSize: "12px",
              fontWeight: 700,
              padding: "10px",
            }}
          />
        </Stack>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            color: "#000",
            fontSize: "15px",
            mb: 2,
          }}
        >
          {api?.description ||
            "A comprehensive video-based KYC solution that enables real-time customer verification through secure, compliant, and automated video interactions."}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};

CategoryCard.propTypes = {
  api: PropTypes.shape({
    name: PropTypes.string,
    category: PropTypes.string,
    description: PropTypes.string,
    subscribe: PropTypes.bool,
  }),
  user: PropTypes.shape({
    role: PropTypes.string,
    jwt: PropTypes.string,
  }),
};

export const ProductCatalog = () => {
  const { user } = useSelector((state) => state.auth);
  const [apiList, setApiList] = useState([]);
  const [loading, setLoading] = useState(true);

  const transformApiDataToFlatList = (groupedData) =>
    Object.entries(groupedData).flatMap(
      ([categoryName, categoryObj]) =>
        categoryObj.apis.map((api) => ({
          ...api,
          category: categoryName,
        }))
    );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getAllApisWithSubscription();
        if (res?.status === 200) {
          const categories =
            res?.data?.data?.attributes?.apisByCategory || {};
          setApiList(transformApiDataToFlatList(categories));
        }
      } catch (error) {
        Logs(error);
        try {
          const fallbackRes = await apiCategortList();
          if (fallbackRes?.status === 200) {
            const categories =
              fallbackRes?.data?.data?.attributes?.apisByCategory || {};
            setApiList(transformApiDataToFlatList(categories));
          }
        } catch (fallbackError) {
          Logs(fallbackError);
        }
      } finally {
      setLoading(false);
    }
    };

    fetchData();
  }, []);

  const vkycApis = apiList.filter((api) =>
    api?.name?.toLowerCase().includes("vkyc")
  );

  return (
    <Wrapper>
      {loading && <Loader />}

      <Box sx={{ mt: { xs: 10, sm: 12, md: 12 }, mb: 2 }}>
      {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: "#65758B",
              fontSize: "14px",
            }}
          >
          Explore our suite of advanced digital verification and onboarding products, 
          designed to streamline compliance, enhance user experience, and accelerate your business growth.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {vkycApis.map((api) => (
            <CategoryCard
              key={api.id}
              api={api}
              user={user}
            />
          ))}
        </Box>
      </Box>
    </Wrapper>
  );
};

 